/*
  Buscador propio (bbx) — A Granel Madrid
  ---------------------------------------
  Motor de búsqueda tolerante: entiende frases, faltas de ortografía, plurales y, en el modo «necesidad»
  (decisión DX de Marco), sinónimos coloquiales y necesidades («algo para dormir», «hierro y magnesio»).

  Cómo funciona
  1. Descarga una vez el índice del catálogo (/collections/all?view=bb-json) y lo guarda en sessionStorage.
  2. Lo que coincide con el NOMBRE de un producto manda (ronda 1 de QA, 30/09): si la consulta, o un tramo de
     ella, es el principio de algún título («azúcar de coco», «jengibre», «azafrán»), se busca como producto y
     el diccionario no la traduce a una necesidad. Después, frases y palabras del diccionario
     (bbx-diccionario.js, opcional: sin él solo se busca por producto y nutriente) y, por último, el corrector.
  3. Puntúa por etiquetas + título + tipo. El producto cuyo nombre empieza por lo buscado va primero y el
     derivado (harina, chocolate, espiral…) detrás del producto base, salvo que se pida.
  4. Si nada casa, lo dice («No tenemos nada con «X»») y propone búsquedas que sí dan resultado. Ya no
     enseña productos al azar como «parecidos» (M19). Si solo casa una parte («maíz palomitas»), también lo dice
     antes de los resultados: «No tenemos «palomitas». Esto es lo que hay con «maíz»» (QA ronda 2, punto 4).
  5. Nutrientes (QA ronda 2, punto 5; M37): un producto solo sale por «hierro», «vitamina C», «fibra» o
     «proteínas» si su tabla nutricional lo respalda (declaraciones calculadas, «b» del índice) o si
     custom.nutrientes_acreditados lo recoge («na»). Las etiquetas de nutriente puestas por IA no cuentan.
     Las necesidades (etiquetas bf-*, decisión DX) siguen igual.

  La tarjeta es la del catálogo (agm-product-card: .pd__card, precio de ag-top-catalogo-precio y «Añadir» de
  quick-add-granel, cuyo código es global y engancha las tarjetas nuevas solo). Aquí no hay estilos de tarjeta.
  Los sellos (Ecológico, Vegano, Sin azúcar añadido, Proteína; tanda 2, 01/10) repiten el marcado y las reglas de
  snippets/ag-t2-catalogo-sellos.liquid, y se estilan con .agm-sello de product-card-granel.css.
  Movimiento = Astra: este archivo no anima nada; los desplazamientos son instantáneos.
*/
(function () {
  'use strict';
  // W permite cargar este archivo en Node (banco de pruebas) además del navegador.
  var W = typeof window !== 'undefined' ? window : (globalThis.window = {});
  if (W.__bbxInit) return;
  W.__bbxInit = true;

  var IDX_KEY = 'bbx-idx-v5';          // v5: + café (m) y nutrientes acreditados (na); b y na vacíos si la ficha no declara
  var IDX_TTL = 30 * 60 * 1000;         // 30 min
  var PAGE_URL = '/pages/buscador';
  var CATALOGO_URL = '/blogs/productos';

  /* ════════════════════════════════════════════════════════════
     1. Utilidades de texto
     ════════════════════════════════════════════════════════════ */

  function norm(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Singular aproximado del español: «nueces» → «nuez», «dátiles» → «datil», «legumbres» → «legumbre»,
  // «garbanzos» → «garbanzo». Se aplica igual a la consulta y a los títulos, así que basta con que sea estable.
  function stem(w) {
    if (w.length > 4 && /ces$/.test(w)) return w.slice(0, -3) + 'z';
    if (w.length > 4 && /[aeiou][lnrdj]es$/.test(w)) return w.slice(0, -2);
    if (w.length > 3 && /s$/.test(w) && !/(is|us|ss)$/.test(w)) return w.slice(0, -1);
    return w;
  }

  // Misma palabra salvo número o género («texturizado» / «texturizada»): no merece aviso de corrección.
  function mismaRaiz(a, b) {
    a = stem(a); b = stem(b);
    return a === b || (a.length > 4 && a.slice(0, -1) === b.slice(0, -1));
  }

  // Distancia Damerau-Levenshtein acotada: sale antes si supera max.
  function dist(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    if (a === b) return 0;
    var prev2 = null, prev = new Array(lb + 1), cur = new Array(lb + 1), i, j, tmp;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var best = i;
      for (j = 1; j <= lb; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < best) best = v;
      }
      if (best > max) return max + 1;
      tmp = prev2; prev2 = prev; prev = cur; cur = tmp || new Array(lb + 1);
    }
    return prev[lb];
  }

  // En palabras cortas una distancia de 2 ya es otra palabra («bonito» → «bocio»): solo se permite 1.
  function maxDist(len) {
    if (len <= 4) return 0;
    if (len <= 7) return 1;
    if (len <= 10) return 2;
    return 3;
  }

  /* ════════════════════════════════════════════════════════════
     2. Vocabulario, etiquetas visibles y reglas de pantalla
     ════════════════════════════════════════════════════════════ */

  var LABELS = {
    'bf-digestion': 'Digestión', 'bf-transito': 'Tránsito', 'bf-gases': 'Hinchazón',
    'bf-acidez': 'Acidez', 'bf-microbiota': 'Microbiota', 'bf-nauseas': 'Náuseas',
    'bf-energia': 'Energía', 'bf-deporte': 'Deporte', 'bf-musculo': 'Músculo',
    'bf-concentracion': 'Concentración', 'bf-sueno': 'Descanso', 'bf-relax': 'Relax',
    'bf-animo': 'Ánimo', 'bf-defensas': 'Defensas', 'bf-respiratorio': 'Respiratorio',
    'bf-colesterol': 'Colesterol', 'bf-corazon': 'Corazón', 'bf-tension': 'Tensión',
    'bf-circulacion': 'Circulación', 'bf-glucosa': 'Glucosa', 'bf-metabolismo': 'Metabolismo',
    'bf-saciedad': 'Saciedad', 'bf-huesos': 'Huesos', 'bf-articulaciones': 'Articulaciones',
    'bf-piel': 'Piel', 'bf-pelo-unas': 'Pelo y uñas', 'bf-antiedad': 'Antiedad',
    'bf-antioxidante': 'Antioxidante', 'bf-antiinflamatorio': 'Antiinflamatorio',
    'bf-depurativo': 'Depurativo', 'bf-diuretico': 'Diurético',
    'bf-antibacteriano': 'Antibacteriano', 'bf-vias-urinarias': 'Vías urinarias',
    'bf-menopausia': 'Menopausia', 'bf-menstruacion': 'Ciclo femenino',
    'bf-embarazo': 'Embarazo', 'bf-prostata': 'Próstata', 'bf-libido': 'Vitalidad',
    'bf-infantil': 'Para niños', 'bf-senior': 'Séniors', 'bf-vista': 'Vista',
    'bf-tiroides': 'Tiroides', 'bf-hidratacion': 'Hidratación',
    'bf-sin-cafeina': 'Sin cafeína', 'bf-estimulante': 'Estimulante',
    'antioxidantes': 'Antioxidante', 'fibra': 'Fibra', 'hierro': 'Hierro',
    'magnesio': 'Magnesio', 'calcio': 'Calcio', 'potasio': 'Potasio',
    'fosforo': 'Fósforo', 'zinc': 'Zinc', 'selenio': 'Selenio', 'yodo': 'Yodo',
    'manganeso': 'Manganeso', 'cobre': 'Cobre', 'cromo': 'Cromo',
    'omega-3': 'Omega 3', 'omega-6': 'Omega 6', 'alto-en-proteina': 'Proteínas',
    'triptofano': 'Triptófano', 'melatonina': 'Melatonina',
    'sin-azucar-anadido': 'Sin azúcares añadidos', 'sin-gluten': 'Sin gluten',
    'vegano': 'Vegano', 'ecologico': 'Ecológico', 'superalimento': 'Superalimento',
    'digestion': 'Digestión', 'energia': 'Energía', 'saciante': 'Saciedad',
    'relajante': 'Relax'
  };

  /*
    Términos de dolencia o de efecto sobre el organismo que NUNCA se repiten en pantalla (Reg. CE 1924/2006;
    auditoría legal del 18/08 y ronda 1 de QA): se sigue buscando por ellos (decisión DX), pero «Buscando:»
    no los escribe. Son los mismos que los atajos llevan escondidos en data-q.
  */
  var NO_EN_PANTALLA = Object.create(null);
  ('bf-colesterol bf-tension bf-glucosa bf-tiroides bf-prostata bf-menopausia bf-embarazo bf-vias-urinarias ' +
    'bf-corazon bf-menstruacion bf-antiinflamatorio bf-antibacteriano bf-depurativo bf-diuretico bf-antiedad ' +
    'bf-acidez bf-gases bf-nauseas bf-libido').split(' ').forEach(function (t) { NO_EN_PANTALLA[t] = true; });

  // Necesidades de enfermedad que se retiran del todo con el modo «necesidad-segura» (preparado, apagado).
  var ENFERMEDAD = Object.create(null);
  ('bf-colesterol bf-tension bf-glucosa bf-tiroides bf-prostata bf-menopausia bf-embarazo bf-vias-urinarias ' +
    'bf-corazon bf-menstruacion').split(' ').forEach(function (t) { ENFERMEDAD[t] = true; });

  /*
    Nutrientes (QA ronda 2, punto 5; M37 y plan A2): solo cuentan con respaldo. Por cada etiqueta, las
    declaraciones de la tabla (b, de ag-declaraciones-lista) que la sostienen y el slug que puede traer
    custom.nutrientes_acreditados (na). Omega 3, triptófano y melatonina no tienen declaración posible en la
    tabla: por etiqueta no sale nada (sí por el título).
  */
  var NUTRIENTE = Object.create(null);
  NUTRIENTE['fibra'] = { b: ['alto contenido en fibra', 'fuente de fibra'], na: 'fibra' };
  NUTRIENTE['alto-en-proteina'] = { b: ['alto contenido en proteinas', 'fuente de proteinas'], na: 'proteinas' };
  ('hierro magnesio calcio potasio fosforo zinc cobre selenio manganeso yodo cromo').split(' ').forEach(function (m) {
    NUTRIENTE[m] = { b: ['fuente de ' + m], na: m };
  });
  ('a d e k c b1 b2 b3 b6 b9 b12').split(' ').forEach(function (v) {
    NUTRIENTE['vitamina-' + v] = { b: ['fuente de vitamina ' + v], na: 'vitamina-' + v };
  });
  ['omega-3', 'omega-6', 'triptofano', 'melatonina'].forEach(function (t) { NUTRIENTE[t] = { b: [], na: null }; });

  // 0 = sin respaldo; 2 = «alto contenido» (va delante); 1 = «fuente de» o acreditado.
  function respaldado(p, tag) {
    var n = NUTRIENTE[tag];
    for (var i = 0; i < n.b.length; i++) if (p._bl.indexOf(n.b[i]) > -1) return n.b.length > 1 && !i ? 2 : 1;
    return n.na && p._na.indexOf(n.na) > -1 ? 1 : 0;
  }

  // «Sin gluten» no se busca (D8: la tienda no lo garantiza): se contesta con un aviso.
  var SIN_GLUTEN = 'sin-gluten';

  // Tags que el motor considera "buscables" aunque no estén en el diccionario.
  var CANON = Object.keys(LABELS);
  var CANON_SET = Object.create(null);
  CANON.forEach(function (t) { CANON_SET[t] = true; });

  function label(tag) {
    if (LABELS[tag]) return LABELS[tag];
    if (/^vitamina-/.test(tag)) return 'Vit. ' + tag.slice(9).toUpperCase();
    return tag.charAt(0).toUpperCase() + tag.slice(1).replace(/-/g, ' ');
  }

  // Etiqueta visible → tag. Garantiza que los atajos siempre resuelvan, aunque el diccionario no esté.
  var REV = Object.create(null);
  Object.keys(LABELS).forEach(function (t) {
    var k = norm(LABELS[t]);
    if (!REV[k]) REV[k] = t;
  });
  REV['proteina'] = REV['proteinas'] = 'alto-en-proteina';
  ['vitamina-a', 'vitamina-b1', 'vitamina-b2', 'vitamina-b3', 'vitamina-b5', 'vitamina-b6',
    'vitamina-b9', 'vitamina-b12', 'vitamina-c', 'vitamina-d', 'vitamina-e', 'vitamina-k'
  ].forEach(function (t) {
    if (!CANON_SET[t]) { CANON_SET[t] = true; CANON.push(t); }
    REV[t.replace('-', ' ')] = t;
  });

  var STOPWORDS = ('a al algo alguna algunas alguno algunos ante antes aqui asi buenas bueno buenos ' +
    'cada como con contra cosa cosas cual cuales cuando dame dar de del desde donde dos e el ella ' +
    'ellas ello ellos en encima entre era eran es esa esas ese eso esos esta estan estar estas este ' +
    'esto estos estoy fue fuera gustaria ha hace hacer hacia han hasta hay he la las le les lo los ' +
    'mas me mejor menos mi mia mis mucha muchas mucho muchos muy nada ni no nos nosotros o os otra ' +
    'otras otro otros para pero poco por porque pues que quiero quisiera quiza recomienda recomiendas ' +
    'se ser si siempre sido sobre solo son su sus tambien tan tanto tener tengo ti tiene tienen toda ' +
    'todas todo todos tu tus un una unas uno unos usted va vais van vaya ver y ya yo hola buenas ' +
    'necesito busco buscando ando quisiera ayuda ayudar ayude sirva sirve servir vale bien ' +
    'pal pa comprar compro compra tomar tomo tomarme uso usar poner hacer haciendo dias noches ' +
    'ultimamente siempre veces vez rato poquito bastante super demasiado gustaria encantaria ' +
    'porfa favor gracias oye mira pues bueno vamos venga ' +
    // compra: no describen el producto (sin esto, «garbanzos baratos» avisaría de que no hay «baratos»)
    'barato barata baratos baratas oferta ofertas precio precios granel kilo kilos ' +
    // formas verbales frecuentes: sin esto el corrector las confunde con términos del diccionario
    'tenga tengan tengas tuviera tuviese hubiera hubiese seas sean fuese siendo estando ' +
    'vaya vayan pueda puedan puede pueden podria podrian querria quiere quieren').split(' ');
  var STOP = Object.create(null);
  STOPWORDS.forEach(function (w) { STOP[w] = true; });

  // Palabras cortas que SÍ son producto y no deben caer como palabra vacía.
  var PROTEGIDAS = Object.create(null);
  ('te mate sal ajo sol soja uva kiwi higo nuez anis coco lino chia mijo malta cola dulce ' +
    'amargo negro verde blanco rojo azul').split(' ').forEach(function (w) { PROTEGIDAS[w] = true; });

  function vacia(w) { return !!STOP[w] && !PROTEGIDAS[w]; }

  // Nombres que la tienda usa distinto que el cliente (sin inventar productos: solo apuntan a los que hay).
  var ALIAS = { orejon: 'albaricoque', kinoa: 'quinoa', quinua: 'quinoa', frijol: 'alubia', judia: 'alubia' };
  var ALIAS_FRASE = { 'soja texturizada': 'proteina vegetal' };
  // Familias que el cliente nombra en conjunto (raíces; con espacio, tramo del título)
  var GRUPOS = {
    'fruto rojo': ['arandano', 'fresa', 'frambuesa', 'goji', 'grosella', 'cereza', 'mora', 'fruta del bosque'],
    'fruta roja': ['arandano', 'fresa', 'frambuesa', 'goji', 'grosella', 'cereza', 'mora', 'fruta del bosque'],
    // Momento de consumo (QA ronda 2, punto 6: «para el desayuno» contestaba «No tenemos nada»). No es una
    // necesidad ni un efecto: son los productos que se toman en el desayuno. Va en el motor (y no en
    // bbx-diccionario.js) para que funcione también sin diccionario, en el modo «ingredientes».
    'desayuno': ['muesli', 'copo', 'avena', 'cacao', 'cafe', 'miel'],
    'desayunar': ['muesli', 'copo', 'avena', 'cacao', 'cafe', 'miel']
  };
  // Grupos cuyo orden importa: lo primero de la lista va antes (muesli y copos antes que la miel).
  var GRUPOS_ORDEN = { desayuno: true, desayunar: true };

  // Sin resultados: búsquedas que sí existen en el catálogo y se parecen a lo que se pedía.
  var ALTERNATIVAS = {
    tofu: [['Proteína vegetal', 'proteina vegetal'], ['Soja', 'soja']],
    tempeh: [['Soja', 'soja'], ['Proteína vegetal', 'proteina vegetal']],
    seitan: [['Gluten de trigo', 'gluten de trigo'], ['Proteína vegetal', 'proteina vegetal']]
  };
  var IDEAS_VACIO = [['Garbanzos', 'garbanzos'], ['Lentejas', 'lentejas'], ['Paella', 'paella']];

  // Derivados que van detrás del producto base cuando no se piden («almendra» → antes la cruda que la bañada).
  // Palabras que no cambian el producto («Nuez Pelada», «Nuez con Cáscara», «Almendra Cruda»): un título que solo
  // añade estas al nombre buscado es el producto base y va delante de las variedades (QA ronda 2, punto 3).
  var ESTADO = Object.create(null);
  ('pelada pelado cascara entera entero natural cruda crudo granel').split(' ').forEach(function (w) { ESTADO[w] = true; });

  var DERIVADO = /\b(chocolate|banad[oa]s?|harinas?|cremas?|espiral(es)?|tallarin(es)?|cous|rigatoni|macarron(es)?|fideos?|galletas?|barritas?|tostad[oa]s?|frit[oa]s?|revuelto|cortezas?|bocados?|inflad[oa]s?|latte|pastas?|snacks?|rellen[oa]s?|crosti|garrapinad[oa]s?|caramelizad[oa]s?)\b/g;

  /* ════════════════════════════════════════════════════════════
     3. Índice del catálogo
     ════════════════════════════════════════════════════════════ */

  var index = null;          // productos decorados (ver decorate)
  var lexicon = null;        // palabras (y raíces) que aparecen en los títulos
  var prefijos = null;       // principios de título en raíces: «azucar», «azucar de», «azucar de coco»…
  var indexPromise = null;
  var indexFailed = false;
  // R21-1c: opcional; CPN inline lo solicita únicamente tras interacción.
  var dictionaryPromise = null;
  var dictionaryFailed = false;

  function loadDictionary(root) {
    var url = root && root.dataset && root.dataset.bbxDictUrl;
    if (!url || W.BBX_DICC || dictionaryFailed) return Promise.resolve();
    if (dictionaryPromise) return dictionaryPromise;
    dictionaryPromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = url;
      s.async = false;
      s.onload = resolve;
      s.onerror = function () { dictionaryFailed = true; resolve(); };
      document.head.appendChild(s);
    });
    return dictionaryPromise;
  }

  function decorate(items) {
    lexicon = Object.create(null);
    prefijos = Object.create(null);
    items.forEach(function (p) {
      p._t = norm(p.t);
      p._y = norm(p.y);
      var todas = p._t.split(' ').filter(Boolean);
      var raices = todas.map(stem);
      p._w = todas.filter(function (w) { return w.length > 2; });
      p._sw = p._w.map(stem);
      p._f0 = raices[0] || '';
      // Dos y tres letras solo son prefijos del primer nombre, nunca del léxico completo.
      if (p._f0.length >= 2) prefijos[p._f0.slice(0, 2)] = true;
      if (p._f0.length >= 3) prefijos[p._f0.slice(0, 3)] = true;
      p._sj = raices.join(' ');
      p._der = (p._t.match(DERIVADO) || []);
      p._w.forEach(function (w, k) { lexicon[w] = true; lexicon[p._sw[k]] = true; });
      for (var n = 1; n <= Math.min(5, raices.length); n++) prefijos[raices.slice(0, n).join(' ')] = true;
      p._g = (p.g || []).map(function (t) { return String(t).toLowerCase(); });
      p._gs = [];
      p._g.forEach(function (t) { t.split('-').forEach(function (x) { if (x.length > 2) p._gs.push(stem(x)); }); });
      p._ys = p._y.split(' ').map(stem);
      // Declaraciones nutricionales calculadas en el índice: «fibra», «proteína», «hierro»…
      p._b = norm((p.b || []).join(' '));
      p._bl = (p.b || []).map(norm);
      p._na = (p.na || []).map(function (x) { return norm(x).replace(/ /g, '-'); });
    });
    return items;
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(IDX_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.ts || (Date.now() - obj.ts) > IDX_TTL) return null;
      if (!Array.isArray(obj.items) || !obj.items.length) return null;
      return obj.items;
    } catch (e) { return null; }
  }

  function writeCache(items) {
    try { sessionStorage.setItem(IDX_KEY, JSON.stringify({ ts: Date.now(), items: items })); }
    catch (e) { /* cuota llena: seguimos sin caché */ }
  }

  function fetchPage(page) {
    return fetch('/collections/all?view=bb-json&sort_by=best-selling&page=' + page, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    var cached = readCache();
    if (cached) {
      index = decorate(cached);
      indexPromise = Promise.resolve(index);
      return indexPromise;
    }
    indexPromise = fetchPage(1).then(function (first) {
      var pages = Math.max(1, Math.min(first.pages || 1, 40));
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (more) {
        var all = first.products || [];
        more.forEach(function (d) { all = all.concat(d.products || []); });
        writeCache(all);
        index = decorate(all);
        return index;
      });
    }).catch(function (e) {
      indexFailed = true;
      throw e;
    });
    return indexPromise;
  }

  /* ════════════════════════════════════════════════════════════
     4. Interpretación de la consulta
     ════════════════════════════════════════════════════════════ */

  function dicc() {
    return W.BBX_DICC || { frases: {}, palabras: {} };
  }

  // Corrige «vitamina» y compañía ANTES de los patrones químicos: si no, «vitamna c» nunca sería «vitamina-c».
  var FORMAS = [
    { canon: 'vitamina', min: 5, max: 11, tol: 2 },
    { canon: 'omega', min: 4, max: 7, tol: 1 },
    { canon: 'proteina', min: 6, max: 11, tol: 2 },
    { canon: 'mineral', min: 5, max: 9, tol: 1 },
    { canon: 'antioxidante', min: 9, max: 15, tol: 3 }
  ];

  function fixFormas(s, fixes) {
    return s.split(' ').map(function (w) {
      if (!w || w.indexOf('-') > -1) return w;
      for (var i = 0; i < FORMAS.length; i++) {
        var f = FORMAS[i];
        if (w === f.canon || w.length < f.min || w.length > f.max) continue;
        if (w.charAt(0) !== f.canon.charAt(0)) continue;
        if (dist(w, f.canon, f.tol) <= f.tol) {
          if (fixes && !mismaRaiz(w, f.canon)) fixes.push({ from: w, to: f.canon });
          return f.canon;
        }
      }
      return w;
    }).join(' ');
  }

  // Normaliza formas de vitaminas, omegas y nombres químicos a slugs canónicos.
  function canonizeChem(s) {
    return s
      // El grupo de la cifra engloba su propio espacio: si no, «vitamina c y zinc» daría «vitamina-cy».
      .replace(/\bvit(?:amina|aminas|s)?\s*\.?\s*-?\s*([abcdek])(?:\s*-\s*|\s*)?(\d{1,2})?\b/g,
        function (m, letra, num) { return 'vitamina-' + letra + (num || ''); })
      .replace(/\bpro\s*vitamina[- ]?a\b/g, 'vitamina-a')
      .replace(/\bomega\s*-?\s*([3569])\b/g, 'omega-$1')
      .replace(/\bacido\s+folico\b/g, 'vitamina-b9')
      .replace(/\bfolatos?\b/g, 'vitamina-b9')
      .replace(/\bacido\s+ascorbico\b/g, 'vitamina-c')
      .replace(/\bcobalamina\b/g, 'vitamina-b12')
      .replace(/\btocoferol(es)?\b/g, 'vitamina-e')
      .replace(/\bretinol\b/g, 'vitamina-a')
      .replace(/\btiamina\b/g, 'vitamina-b1')
      .replace(/\briboflavina\b/g, 'vitamina-b2')
      .replace(/\bniacina\b/g, 'vitamina-b3')
      .replace(/\bpiridoxina\b/g, 'vitamina-b6')
      .replace(/\bcolecalciferol\b/g, 'vitamina-d')
      .replace(/\bfiloquinona\b/g, 'vitamina-k')
      .replace(/\bproteinas?\b/g, 'alto-en-proteina')
      .replace(/\bsin\s+azucar(\s+anadid[oa])?\b/g, 'sin-azucar-anadido')
      .replace(/\bsin\s+gluten\b/g, 'sin-gluten')
      .replace(/\bsin\s+lactosa\b/g, 'vegano');
  }

  // Si «vitamina-d3» no existe, prueba «vitamina-d».
  function fixVitamin(tag) {
    if (CANON_SET[tag]) return tag;
    var m = /^vitamina-([a-k])(\d+)$/.exec(tag);
    if (m) {
      if (CANON_SET['vitamina-' + m[1] + m[2]]) return 'vitamina-' + m[1] + m[2];
      if (CANON_SET['vitamina-' + m[1]]) return 'vitamina-' + m[1];
    }
    if (/^vitamina-[a-k]\d{0,2}$/.test(tag)) return tag;
    return null;
  }

  // Vocabulario contra el que se corrigen las faltas de ortografía. Las palabras de los títulos van
  // delante del diccionario: ante un empate, gana el producto.
  var fuzzyPool = null;
  function buildFuzzyPool() {
    var d = dicc(), pool = Object.create(null), k;
    if (lexicon) for (k in lexicon) pool[k] = 'texto';
    for (k in d.palabras) if (!pool[k]) pool[k] = 'dicc';
    CANON.forEach(function (t) { var w = t.replace(/^bf-/, ''); if (!pool[w]) pool[w] = 'tag:' + t; });
    fuzzyPool = Object.keys(pool).map(function (w) { return { w: w, kind: pool[w], len: w.length }; });
    return fuzzyPool;
  }

  function fuzzyLookup(token) {
    if (!fuzzyPool) buildFuzzyPool();
    var max = maxDist(token.length);
    if (max === 0) return null;
    var best = null, bestD = max + 1;
    for (var i = 0; i < fuzzyPool.length; i++) {
      var c = fuzzyPool[i];
      if (Math.abs(c.len - token.length) > max) continue;
      var d = dist(token, c.w, max);
      if (d < bestD || (d === bestD && best && c.kind === 'texto' && best.kind !== 'texto')) {
        bestD = d; best = c;
      }
    }
    return bestD <= max ? best : null;
  }

  /*
    Devuelve { concepts:[{kind:'tag'|'text', v, w, head}], fixes:[{from,to}], raw, norm, span, sinGluten }
    opts.chip: la consulta viene de un atajo (necesidad explícita): no se aplica la regla del título.
    opts.sinEnfermedad: modo «necesidad-segura», se descartan las necesidades de enfermedad.
  */
  function parseQuery(q, opts) {
    opts = opts || {};
    var d = dicc();
    var concepts = [], fixes = [];
    var seen = Object.create(null);
    var out = { concepts: concepts, fixes: fixes, raw: String(q || '').trim(), norm: '', span: '', sinGluten: false };

    function push(kind, v, w, head, tit) {
      if (!v) return;
      var o = v;   // lo que escribió el cliente (para el aviso de «No tenemos…»)
      if (kind === 'tag' && v === SIN_GLUTEN) { out.sinGluten = true; return; }
      if (kind === 'tag' && opts.sinEnfermedad && ENFERMEDAD[v]) return;
      if (kind === 'text') v = stem(v);
      var key = kind + ':' + v;
      if (seen[key]) { if (w > seen[key].w) seen[key].w = w; return; }
      var c = { kind: kind, v: v, w: w, head: !!head, tit: !!tit, o: o };
      seen[key] = c;
      concepts.push(c);
    }
    function pushTags(list, w) {
      // Modo «necesidad-segura»: si la expresión lleva una enfermedad, fuera entera (también el nutriente que la acompaña).
      if (opts.sinEnfermedad && (list || []).some(function (t) { return ENFERMEDAD[t]; })) return;
      (list || []).forEach(function (t) {
        var fixed = CANON_SET[t] ? t : fixVitamin(t) || t;
        push('tag', fixed, w);
      });
    }

    // «sin gluten», «sin azúcar», «sin lactosa» son una sola idea: se juntan antes de mirar los títulos
    // (si no, «gluten» casaría con «Gluten de Trigo»).
    var toks = norm(q)
      .replace(/\bsin\s+azucar(es)?(\s+anadid[oa]s?)?\b/g, 'sin-azucar-anadido')
      .replace(/\bsin\s+gluten\b/g, 'sin-gluten')
      .replace(/\bsin\s+lactosa\b/g, 'vegano')
      .split(' ').filter(Boolean);

    // 4.0 — familias y momentos («frutos rojos», «para el desayuno»): también desde un atajo
    var unidoG = toks.map(stem).join(' ');
    Object.keys(GRUPOS).forEach(function (g) {
      if ((' ' + unidoG + ' ').indexOf(' ' + g + ' ') > -1) {
        var c = { kind: 'grupo', v: GRUPOS[g], w: 1, head: true, tit: true, o: g, ord: !!GRUPOS_ORDEN[g] };
        seen['grupo:' + g] = c; concepts.push(c);
        unidoG = (' ' + unidoG + ' ').replace(' ' + g + ' ', ' ').trim();
        toks = unidoG.split(' ').filter(Boolean);
      }
    });

    // 4.1 — nombres de la tienda y nombres de producto (mandan sobre el diccionario)
    var resto = toks;
    if (!opts.chip) {
      var unido = toks.map(stem).join(' ');
      Object.keys(ALIAS_FRASE).forEach(function (f) {
        if (unido.indexOf(f) > -1) { fixes.push({ from: f, to: ALIAS_FRASE[f] }); unido = unido.replace(f, ALIAS_FRASE[f]); toks = unido.split(' '); }
      });
      toks = toks.filter(Boolean).map(function (w) {
        var a = ALIAS[stem(w)];
        if (a) { fixes.push({ from: w, to: a }); return a; }
        return w;
      });
      resto = [];
      if (prefijos) {
        for (var i = 0; i < toks.length;) {
          var hit = 0;
          // Solo una consulta corta aislada puede convertir «le» o «te» en nombre de producto.
          if (!vacia(toks[i]) || (toks.length === 1 && toks[i].length === 2 && prefijos[stem(toks[i])])) {
            for (var n = Math.min(5, toks.length - i); n >= 1; n--) {
              if (prefijos[toks.slice(i, i + n).map(stem).join(' ')]) { hit = n; break; }
            }
          }
          if (hit) {
            var tramo = toks.slice(i, i + hit);
            var raiz = tramo.map(stem).join(' ');
            if (raiz.length > out.span.length) out.span = raiz;
            tramo.forEach(function (w, k) { if ((!vacia(w) || (toks.length === 1 && w.length === 2 && prefijos[stem(w)])) && w.length >= 2) push('text', w, 1, k === 0, true); });
            i += hit;
          } else {
            resto.push(toks[i]);
            i++;
          }
        }
      } else {
        resto = toks;
      }
    }

    var text = canonizeChem(fixFormas(resto.join(' '), fixes));
    out.norm = norm(q);
    var tokens = text.split(' ').filter(Boolean);
    var used = new Array(tokens.length);

    // 4.2 — frases del diccionario, de más larga a más corta
    var maxN = Math.min(6, tokens.length);
    for (var n2 = maxN; n2 >= 2; n2--) {
      for (var i2 = 0; i2 + n2 <= tokens.length; i2++) {
        var libre = true;
        for (var k2 = i2; k2 < i2 + n2; k2++) if (used[k2]) { libre = false; break; }
        if (!libre) continue;
        var frase = tokens.slice(i2, i2 + n2).join(' ');
        var hitFrase = d.frases[frase] || (REV[frase] ? [REV[frase]] : null);
        if (hitFrase) {
          pushTags(hitFrase, 1);
          for (var m = i2; m < i2 + n2; m++) used[m] = true;
        }
      }
    }

    // 4.3 — tokens sueltos
    for (var t = 0; t < tokens.length; t++) {
      if (used[t]) continue;
      var tok = tokens[t];
      if (tok.length < 2) continue;

      if (CANON_SET[tok]) { pushTags([tok], 1); continue; }
      var vit = /^vitamina-/.test(tok) ? fixVitamin(tok) : null;
      if (vit) { pushTags([vit], 1); continue; }
      if (d.palabras[tok]) { pushTags(d.palabras[tok], 1); continue; }
      if (REV[tok]) { pushTags([REV[tok]], 1); continue; }
      if (CANON_SET['bf-' + tok]) { pushTags(['bf-' + tok], 1); continue; }

      var sing = stem(tok);
      if (sing !== tok && sing.length > 2) {
        if (d.palabras[sing]) { pushTags(d.palabras[sing], 1); continue; }
        if (CANON_SET[sing]) { pushTags([sing], 1); continue; }
        if (CANON_SET['bf-' + sing]) { pushTags(['bf-' + sing], 1); continue; }
      }

      // Las palabras vacías se descartan antes de mirar el catálogo («para», «natural»…).
      if (vacia(tok)) continue;

      // palabra que aparece en algún título del catálogo → búsqueda por texto
      if (lexicon && (lexicon[tok] || lexicon[sing])) { push('text', tok, 1); continue; }

      // corrección ortográfica; si la palabra corregida es nombre de producto, se busca como producto
      var hit2 = fuzzyLookup(tok);
      if (hit2) {
        if (!mismaRaiz(tok, hit2.w)) fixes.push({ from: tok, to: hit2.w });
        // «almendars» → «almendra»: si la palabra corregida es principio de título, cuenta como tal (y desempata igual)
        if (prefijos && prefijos[stem(hit2.w)] && stem(hit2.w).length > out.span.length && !out.span) out.span = stem(hit2.w);
        if (hit2.kind === 'texto' || (prefijos && prefijos[stem(hit2.w)])) push('text', hit2.w, 0.85, !!(prefijos && prefijos[stem(hit2.w)]));
        else if (hit2.kind === 'dicc') pushTags(d.palabras[hit2.w], 0.85);
        else pushTags([hit2.kind.slice(4)], 0.85);
        continue;
      }

      // no sabemos qué es: texto libre de poco peso
      if (tok.length >= 3) push('text', tok, 0.6);
    }

    // Con una necesidad en la consulta («té para dormir») manda la necesidad, no el nombre del producto.
    out.hayNecesidad = concepts.some(function (c) { return c.kind === 'tag'; });
    return out;
  }

  /* ════════════════════════════════════════════════════════════
     5. Puntuación y búsqueda
     ════════════════════════════════════════════════════════════ */

  function scoreProduct(p, concepts, parsed) {
    var s = 0, matched = [], hayTexto = false;
    for (var i = 0; i < concepts.length; i++) {
      var c = concepts[i], got = 0;
      if (c.kind === 'tag') {
        if (NUTRIENTE[c.v]) { var rn = respaldado(p, c.v); if (rn) got = 10 + rn; else if (p._t.indexOf(c.v) > -1) got = 5; }
        else if (p._g.indexOf(c.v) > -1) got = 12;
        else if (c.v === 'bf-antioxidante' && p._g.indexOf('antioxidantes') > -1) got = 12;
        else if (c.v === 'bf-digestion' && p._g.indexOf('digestion') > -1) got = 11;
        else if (c.v === 'bf-energia' && p._g.indexOf('energia') > -1) got = 11;
        else if (c.v === 'bf-saciedad' && p._g.indexOf('saciante') > -1) got = 11;
        else if (c.v === 'bf-relax' && p._g.indexOf('relajante') > -1) got = 11;
        else if (p._t.indexOf(c.v.replace(/^bf-/, '')) > -1) got = 5;
      } else if (c.kind === 'grupo') {
        // «frutos rojos»: cualquiera de la familia (arándano, fresa, frambuesa…)
        hayTexto = true;
        for (var gi = 0; gi < c.v.length; gi++) {
          var gw = c.v[gi];
          if (gw.indexOf(' ') > -1 ? p._sj.indexOf(gw) > -1 : p._sw.indexOf(gw) > -1) {
            got = 10;
            if (p._f0 === gw.split(' ')[0] && !(parsed && parsed.hayNecesidad)) got += 10;
            if (c.ord) got += (c.v.length - gi) * 0.5;
            break;
          }
        }
      } else {
        hayTexto = true;
        var w = c.v;
        // Mantener la puntuación de palabra completa antes del nuevo prefijo corto.
        if (p._sw.indexOf(w) > -1) got = 10;
        // Dos/tres letras solo son prefijo del primer nombre reconocido por parseQuery.
        else if (c.tit && w.length >= 2 && w.length < 4 && p._f0.indexOf(w) === 0) got = 7;
        else if (w.length >= 5 && p._t.indexOf(w) > -1) got = 8;
        else if (p._ys.indexOf(w) > -1) got = 5;
        // Las declaraciones («bajo contenido en azúcares») solo cuentan si no se busca un producto por su nombre
        else if (!c.tit && w.length >= 4 && p._b && p._b.indexOf(w) > -1) got = 6;
        else if (w.length >= 4) {
          for (var j = 0; j < p._sw.length; j++) {
            var pw = p._sw[j];
            if (pw.indexOf(w) === 0) { got = 7; break; }
            if (w.length >= 5 && Math.abs(pw.length - w.length) <= 2 && dist(w, pw, 1) <= 1) { got = 6; break; }
          }
          if (!got && !c.tit && p._gs.indexOf(w) > -1) got = 4;
        }
        // El nombre del producto ES lo buscado («Alubia …» para «alubias blancas»)
        if (got && p._f0 === w && !(parsed && parsed.hayNecesidad)) got += c.head ? 10 : 6;
      }
      if (got) { s += got * c.w; matched.push(c.kind === 'grupo' ? 'grupo' : c.v); }
    }
    if (!s) return null;
    var coverage = matched.length / concepts.length;
    s = s * (0.5 + 0.5 * coverage) + matched.length * 5;
    if (parsed && parsed.span && !parsed.hayNecesidad && p._sj.indexOf(parsed.span) === 0) s += 12;   // el título empieza por lo buscado
    if (hayTexto && p._der.length && parsed) {
      for (var x = 0; x < p._der.length; x++) {
        if (parsed.norm.indexOf(p._der[x]) === -1) { s -= 25; break; }             // derivado que no se ha pedido
      }
    }
    return { p: p, s: s, matched: matched, full: matched.length === concepts.length };
  }

  function runSearch(concepts, parsed) {
    if (!index) return [];
    var out = [];
    for (var i = 0; i < index.length; i++) {
      var r = scoreProduct(index[i], concepts, parsed);
      if (r) { r.idx = i; out.push(r); }
    }
    desempatar(out, parsed);
    out.sort(function (a, b) { return (b.s - a.s) || (a.idx - b.idx); });
    return out;
  }

  /*
    QA ronda 2, punto 3 («nueces» empezaba por la nuez moscada): entre los productos cuyo título empieza por lo
    buscado, (1) la familia que más se llama así (tipo del catálogo) va delante: «nuez» es sobre todo fruto seco,
    no especia; y (2) el producto base («Nuez Pelada», «Nuez con Cáscara») delante de las variedades. Solo
    desempata: la regla del título y la penalización de los derivados siguen mandando.
  */
  function desempatar(out, parsed) {
    if (!parsed || !parsed.span || parsed.hayNecesidad) return;
    var span = parsed.span, n = span.split(' ').length, cuenta = Object.create(null), inicio = [];
    out.forEach(function (r) {
      if (r.p._sj.indexOf(span) !== 0) return;
      inicio.push(r);
      cuenta[r.p._y] = (cuenta[r.p._y] || 0) + 1;
    });
    if (inicio.length < 2) return;
    var tipos = Object.keys(cuenta).sort(function (a, b) { return cuenta[b] - cuenta[a]; });
    var familia = tipos.length > 1 && cuenta[tipos[0]] >= 2 && cuenta[tipos[0]] > cuenta[tipos[1]] ? tipos[0] : null;
    inicio.forEach(function (r) {
      if (familia && r.p._y === familia) r.s += 4;
      var resto = r.p._sj.split(' ').slice(n).filter(function (w) { return w && !vacia(w) && w.length > 2; });
      if (resto.every(function (w) { return ESTADO[w]; })) r.s += 2;
    });
  }

  /*
    QA ronda 2, punto 4 (M19): lo que la consulta pide y NINGÚN resultado tiene. Devuelve null o
    { no: [palabras sin nada], si: [palabras que sí casan] }, con lo que escribió el cliente.
  */
  function faltan(parsed, items) {
    if (!parsed || !items.length || parsed.concepts.length < 2) return null;
    var crudas = Object.create(null);
    String(parsed.raw || '').split(/\s+/).forEach(function (w) { var k = norm(w); if (k) { crudas[k] = w; crudas[stem(k)] = w; } });
    var no = [], si = [];
    parsed.concepts.forEach(function (c) {
      var clave = c.kind === 'grupo' ? 'grupo' : c.v;
      var casa = items.some(function (it) { return it.matched.indexOf(clave) > -1; });
      var ver = c.kind === 'tag' ? (NO_EN_PANTALLA[c.v] ? '' : label(c.v)) : (crudas[c.o] || crudas[c.v] || c.o || '');
      if (!ver) return;
      (casa ? si : no).push(ver);
    });
    return no.length && si.length ? { no: no, si: si } : null;
  }

  // Atajo con familia de producto en la etiqueta («Cereales y avena»): solo se enseña lo que es de esa familia,
  // para que el texto del botón diga la verdad sobre lo que sale. data-f = «tipo:Cereales|avena|…».
  function filtroFamilia(spec) {
    if (!spec) return null;
    var tipos = [], palabras = [];
    String(spec).split('|').forEach(function (x) {
      x = x.trim();
      if (!x) return;
      if (x.indexOf('tipo:') === 0) tipos.push(norm(x.slice(5)));
      else palabras.push(stem(norm(x)));
    });
    return function (p) {
      if (tipos.indexOf(p._y) > -1) return true;
      for (var i = 0; i < palabras.length; i++) {
        if (palabras[i].indexOf(' ') > -1 ? p._sj.indexOf(palabras[i]) > -1 : p._sw.indexOf(palabras[i]) > -1) return true;
      }
      return false;
    };
  }

  /* ════════════════════════════════════════════════════════════
     6. Fallback sin índice (servidor, por tag)
     ════════════════════════════════════════════════════════════ */

  function serverTagSearch(tags) {
    var fetches = tags.slice(0, 4).map(function (t) {
      return fetch('/collections/all/' + encodeURIComponent(t) + '?view=bb-json&sort_by=best-selling&page=1')
        .then(function (r) { return r.json(); })
        .catch(function () { return { products: [] }; });
    });
    return Promise.all(fetches).then(function (res) {
      var map = Object.create(null), order = 0;
      res.forEach(function (d, i) {
        (d.products || []).forEach(function (p) {
          if (!map[p.u]) map[p.u] = { p: p, matched: [], idx: order++, s: 1 };
          if (map[p.u].matched.indexOf(tags[i]) === -1) map[p.u].matched.push(tags[i]);
        });
      });
      var items = Object.keys(map).map(function (k) { return map[k]; });
      items.forEach(function (it) { it.full = it.matched.length === tags.length; });
      items.sort(function (a, b) { return (b.matched.length - a.matched.length) || (a.idx - b.idx); });
      return items;
    });
  }

  /* ════════════════════════════════════════════════════════════
     7. Cesta (para que la tarjeta nazca con «✓ 250 g en tu cesta», como la del servidor)
     ════════════════════════════════════════════════════════════ */

  var carrito = null, carritoPromesa = null;
  function formatoVariante(t) {
    t = String(t || '').trim();
    if (!t || t === 'Default Title') return '';
    var m = t.replace(/\s+/g, '').match(/^([\d.,]+)(kg|ml|cl|g|l)$/i);
    return m ? m[1].replace('.', ',') + ' ' + m[2].toLowerCase() : t;
  }
  function mapaCarrito(c) {
    var m = Object.create(null);
    ((c && c.items) || []).forEach(function (it) {
      if (!m[it.product_id]) m[it.product_id] = { key: it.key, qty: it.quantity, f: formatoVariante(it.variant_title) };
    });
    return m;
  }
  function cargarCarrito() {
    if (carritoPromesa) return carritoPromesa;
    carritoPromesa = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { carrito = mapaCarrito(c); return carrito; })
      .catch(function () { carrito = Object.create(null); return carrito; });
    return carritoPromesa;
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('cart:update', function (e) {
      var c = e && e.detail && e.detail.data && e.detail.data.cart;
      if (c && c.items) carrito = mapaCarrito(c);
    });
  }

  /* ════════════════════════════════════════════════════════════
     8. Interfaz
     ════════════════════════════════════════════════════════════ */

  var ICONOS = {
    plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
    minus: '<path d="M5 12h14"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
    // Sellos de la tarjeta (los mismos cuatro trazos que ag-badge-icon: sprout, leaf, nosugar y protein)
    sprout: '<path d="M12 21c0-6 0-9 5-12"/><path d="M12 13C8.5 13 5 11 5 5.5c5 0 7.5 2 7.5 7.5z"/><path d="M12.5 15c3.2 0 7-1.2 7-6.5-5.2 0-7 1.2-7 6.5z"/>',
    leaf: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/>',
    nosugar: '<rect x="4" y="7" width="16" height="10" rx="1.5"/><line x1="5.6" y1="5.6" x2="18.4" y2="18.4"/>',
    protein: '<path d="M12 3.5C8.2 7 6 10.5 6 13.7a6 6 0 0 0 12 0c0-3.2-2.2-6.7-6-10.2z"/><path d="M12 8.5v8.5"/>'
  };
  // Los mismos trazos que snippets/ag-badge-icon.liquid (dueño: global); el marcado es fijo, sin datos.
  function icono(nombre, size) {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('class', 'ag-icon');
    s.setAttribute('width', size); s.setAttribute('height', size);
    s.setAttribute('viewBox', '0 0 24 24'); s.setAttribute('fill', 'none'); s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '1.6'); s.setAttribute('stroke-linecap', 'round'); s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true'); s.setAttribute('focusable', 'false');
    s.innerHTML = ICONOS[nombre];
    return s;
  }

  // Los mismos valores que la ficha y quick-add-granel (properties[Molido]); el primero va marcado.
  var MOLIDOS = [
    ['Grano', 'En grano'],
    ['Molido cafetera expreso', 'Expreso'],
    ['Molido cafetera italiana', 'Italiana'],
    ['Molido cafetera americana (filtro de papel)', 'Americana']
  ];

  function money(cents) {
    var n = (Math.round(cents) / 100).toFixed(2).split('.');
    return n[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + n[1] + ' €';
  }

  function ancho(url, w) {
    return /[?&]width=\d+/.test(url) ? url.replace(/([?&]width=)\d+/, '$1' + w) : url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  // Sellos (tanda 2, 01/10, órdenes de Marco 5 y 8; 2b, 02/10: «ponlas sobre la imagen»). Mismas reglas, mismo
  // marcado y mismo orden que snippets/ag-t2-catalogo-sellos.liquid (dueña: catalogo; estilos .agm-sello en
  // product-card-granel.css y, sobre la foto, en productos-destacados.css): solo los que el producto tiene en sus
  // datos y en orden de prioridad, porque sobre la foto de una tarjeta de móvil caben dos.
  //   Ecológico: etiqueta ecologico, o el título lleva «Ecológic» o la palabra «Eco» (D12; nunca el logo europeo)
  //   Proteína: alto-en-proteina · Vegano: etiqueta vegano · Sin azúcar añadido: sin-azucar-anadido (o sin-azucar)
  function sellos(p) {
    // La respuesta de respaldo por etiquetas no pasa por decorate; conserva los mismos sellos.
    var g = (p._g || (p.g || []).map(function (t) { return String(t).toLowerCase(); })).map(function (t) { return t.trim(); });
    function tiene() { for (var i = 0; i < arguments.length; i++) if (g.indexOf(arguments[i]) > -1) return true; return false; }
    var titulo = ' ' + String(p.t || '').toLowerCase() + ' ';
    var lista = [];
    if (tiene('ecologico', 'ecológico') || /ecológic|ecologic| eco /.test(titulo)) lista.push(['eco', 'sprout', 'Ecológico']);
    if (tiene('alto-en-proteina', 'alta-proteina')) lista.push(['prot', 'protein', 'Proteína']);
    if (tiene('vegano')) lista.push(['veg', 'leaf', 'Vegano']);
    if (tiene('sin-azucar-anadido', 'sin-azucar', 'sin azucar')) lista.push(['sa', 'nosugar', 'Sin azúcar añadido']);
    return lista;
  }

  function init(root) {
    if (root.hasAttribute('data-bbx-ready')) return;
    root.setAttribute('data-bbx-ready', '1');

    var mode = root.dataset.mode || 'inline';
    var pageUrl = root.dataset.pageUrl || PAGE_URL;
    var sinEnfermedad = root.dataset.enfermedad === 'fuera';
    var input = root.querySelector('[data-bbx-input]');
    var searchbox = root.querySelector('.bbx-searchbox');
    var clearBtn = root.querySelector('[data-bbx-clear]');
    var status = root.querySelector('[data-bbx-status]');
    var note = root.querySelector('[data-bbx-note]');
    var grid = root.querySelector('[data-bbx-grid]');
    var moreBtn = root.querySelector('[data-bbx-more]');
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-bbx-chip]'));
    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-bbx-chiptoggle]'));
    var LIMIT = mode === 'full' ? 0 : (mode === 'search' ? 8 : 12);
    var SIZES = mode === 'search'
      ? '(max-width: 749px) 48vw, (max-width: 1199px) 24vw, 290px'
      : '(max-width: 749px) 48vw, (max-width: 989px) 31vw, (max-width: 1199px) 24vw, 220px';
    var timer = null, token = 0, pending = null, chipActivo = null, desplazado = false, tA = 0, ultimoEvento = '';

    function dictionaryReady() {
      return !root.dataset.bbxDictUrl || !!W.BBX_DICC || dictionaryFailed;
    }

    function preparar() {
      if (mode === 'inline') pedirHojaTarjeta();
      return Promise.all([loadDictionary(root), loadIndex(), cargarCarrito()]);
    }

    // El inline ya tiene su motor; calienta datos (diccionario, índice y cesta) fuera de la ruta LCP.
    // Las promesas globales hacen seguro que varios roots o un gesto temprano compartan la misma carga.
    // Coste de DOM (02/10, coste-dom/ESPEC-COSTE.md, medida E): al primer gesto en la página (los mismos de
    // ag-component-loader.js), no al quedar ocioso tras load. Con «Compra según lo que buscas» arriba en
    // /blogs/productos, el calentamiento corría en cada carga, también sin nadie que lo usara (en los pares R27
    // del catálogo, bbx-diccionario.js se pedía en 10 de 10 cargas y script + parseHTML subía +75 ms LHR).
    // Los atajos, el campo y su foco ya llaman a preparar() por su cuenta.
    function programarPreparacionInline() {
      if (mode !== 'inline') return;
      var GESTOS = ['pointerdown', 'keydown', 'touchstart', 'mousemove'];
      var hecho = false;
      var go = function () {
        if (hecho) return;
        hecho = true;
        GESTOS.forEach(function (ev) { W.removeEventListener(ev, go, true); });
        preparar().catch(function () { });
      };
      GESTOS.forEach(function (ev) { W.addEventListener(ev, go, { capture: true, passive: true }); });
    }

    function reintentarPendienteTrasDiccionario() {
      return loadDictionary(root).then(function () {
        if (!pending) return;
        var p = pending;
        pending = null;
        search(p.q, p.opts);
      });
    }

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    // En páginas sin tarjetas del catálogo (/pages/buscador lo carga en su plantilla) se pide su hoja.
    // Medida E: en el inline, al preparar (primer gesto) o al pintar, no al iniciar. Una hoja nueva tras load
    // obliga a recalcular el estilo de toda la página, y en la portada y el catálogo nadie ha pedido tarjetas aún.
    function pedirHojaTarjeta() {
      if (document.querySelector('link[href*="productos-destacados"]')) return;
      var hoja = document.createElement('link');
      hoja.rel = 'stylesheet';
      hoja.href = root.dataset.cardCss || '';
      if (hoja.href) document.head.appendChild(hoja);
    }
    if (mode !== 'inline') pedirHojaTarjeta();

    /* ── la tarjeta del catálogo (agm-product-card) ── */
    function anadir(p, linea) {
      var q = el('div', 'agqa' + (linea ? ' is-incart' : ''));
      q.setAttribute('data-agqa', '');
      q.setAttribute('data-product-id', p.id);
      q.setAttribute('data-name', p.t);
      if (linea) {
        q.setAttribute('data-key', linea.key);
        q.setAttribute('data-qty', linea.qty);
        q.setAttribute('data-formato', linea.f);
      }
      var add = el('div', 'agqa__add');
      if (linea) add.hidden = true;
      var vs = p.v || [];
      var cafe = !!p.m;
      var btn;
      // Café (QA ronda 2, punto 2): como quick-add-granel, el selector se abre siempre, pregunta el molido antes
      // del formato y su código manda properties[Molido] con AG.addToCart (lee [data-agqa-molido] de la tarjeta).
      // Agotados (26/10): sin ningún formato a la venta (p.a === 0), botón directo «Agotado», como en quick-add-granel
      var agotado = p.a === 0;
      if (!agotado && (vs.length > 1 || (cafe && vs.length === 1))) {
        btn = el('button', 'agqa__btn');
        btn.type = 'button';
        btn.setAttribute('data-agqa-toggle', '');
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-label', 'Añadir ' + p.t + ' a la cesta: elige ' + (cafe ? 'el molido y ' : '') + 'el formato');
        btn.appendChild(icono('plus', 16));
        btn.appendChild(document.createTextNode(' Añadir'));
        add.appendChild(btn);
        var pop = el('div', 'agqa__pop');
        pop.setAttribute('data-agqa-pop', '');
        pop.hidden = true;
        var flecha = el('div', 'agqa__arrow');
        flecha.setAttribute('aria-hidden', 'true');
        pop.appendChild(flecha);
        if (cafe) {
          var mol = el('label', 'agqa__mol');
          mol.appendChild(el('span', 'agqa__title', 'Molido'));
          var sel = el('select', 'agqa__mol-sel');
          sel.setAttribute('data-agqa-molido', '');
          MOLIDOS.forEach(function (m, k) {
            var op = el('option', null, m[1]);
            op.value = m[0];
            if (!k) op.selected = true;
            sel.appendChild(op);
          });
          mol.appendChild(sel);
          pop.appendChild(mol);
        }
        pop.appendChild(el('p', 'agqa__title', 'Elige el formato'));
        var opts = el('div', 'agqa__opts');
        opts.setAttribute('role', 'group');
        opts.setAttribute('aria-label', 'Formatos de ' + p.t);
        vs.forEach(function (v) {
          var o = el('button', 'agqa__opt' + (v[3] ? '' : ' is-soldout'));
          o.type = 'button';
          o.setAttribute('data-agqa-variant', v[0]);
          o.setAttribute('data-formato', v[1]);
          if (v[3]) o.setAttribute('aria-label', 'Añadir ' + v[1] + ' de ' + p.t + ' a la cesta, ' + money(v[2]));
          else { o.disabled = true; o.setAttribute('aria-disabled', 'true'); o.setAttribute('aria-label', v[1] + ', agotado'); }
          o.appendChild(el('span', 'agqa__opt-f', v[1]));
          o.appendChild(el('small', null, v[3] ? money(v[2]) : 'Agotado'));
          opts.appendChild(o);
        });
        pop.appendChild(opts);
        add.appendChild(pop);
      } else if (vs.length) {
        btn = el('button', 'agqa__btn');
        btn.type = 'button';
        btn.setAttribute('data-agqa-direct', '');
        btn.setAttribute('data-agqa-variant', vs[0][0]);
        btn.setAttribute('data-formato', vs[0][1]);
        if (p.a) {
          btn.setAttribute('aria-label', 'Añadir ' + p.t + ' a la cesta');
          btn.appendChild(icono('plus', 16));
          btn.appendChild(document.createTextNode(' Añadir'));
        } else {
          btn.disabled = true;
          btn.setAttribute('aria-label', p.t + ': agotado');
          btn.textContent = 'Agotado';
        }
        add.appendChild(btn);
      } else {
        return null;   // índice sin variantes (no debería pasar): la tarjeta queda como enlace
      }
      q.appendChild(add);

      var inc = el('div', 'agqa__incart');
      inc.setAttribute('data-agqa-incart', '');
      inc.setAttribute('role', 'group');
      inc.setAttribute('aria-label', p.t + ' en tu cesta');
      if (!linea) inc.hidden = true;
      var dec = el('button', 'agqa__paso');
      dec.type = 'button';
      dec.setAttribute('data-agqa-dec', '');
      dec.setAttribute('aria-label', linea && linea.qty > 1 ? 'Quitar una unidad' : 'Quitar de la cesta');
      dec.appendChild(icono('minus', 18));
      var cant = el('span', 'agqa__cant');
      var num = el('span', null, linea ? String(linea.qty) : '');
      num.setAttribute('data-agqa-qty', '');
      cant.appendChild(num);
      cant.appendChild(el('span', 'agqa__cant-txt', ' en tu cesta'));
      var mas = el('button', 'agqa__paso');
      mas.type = 'button';
      mas.setAttribute('data-agqa-inc', '');
      mas.setAttribute('aria-label', 'Añadir una unidad');
      mas.appendChild(icono('plus', 18));
      inc.appendChild(dec); inc.appendChild(cant); inc.appendChild(mas);
      q.appendChild(inc);
      return q;
    }

    function card(p) {
      var linea = carrito && p.id ? carrito[p.id] : null;
      var c = el('div', 'pd__card pd__card--grid bbx-card' + (p.a === 0 ? ' pd__card--agotado' : ''));
      var a = el('a', 'pd__card-link');
      a.href = p.u;
      a.setAttribute('aria-label', 'Ver ' + p.t);
      c.appendChild(a);

      var wrap = el('div', 'pd__img-wrap');
      if (p.a === 0) wrap.appendChild(el('span', 'pd__agotado', 'Agotado')); // agotados (26/10), como agm-product-card
      if (linea) {
        var fl = el('span', 'pd__encesta');
        fl.setAttribute('data-agqa-flag', '');
        fl.appendChild(icono('check', 14));
        var ft = el('span', null, (linea.f ? linea.f + ' ' : '') + 'en tu cesta');
        ft.setAttribute('data-agqa-flag-txt', '');
        fl.appendChild(ft);
        wrap.appendChild(fl);
      }
      if (p.i) {
        var img = el('img');
        img.src = ancho(p.i, 400);
        img.srcset = [300, 400, 600, 800].map(function (w) { return ancho(p.i, w) + ' ' + w + 'w'; }).join(', ');
        img.sizes = SIZES;
        img.alt = p.t;
        img.width = 400;
        img.height = 400;
        img.loading = 'lazy';
        img.decoding = 'async';
        wrap.appendChild(img);
      } else {
        wrap.appendChild(el('div', 'pd__placeholder'));
      }
      c.appendChild(wrap);

      var info = el('div', 'pd__info');
      info.appendChild(el('h3', 'pd__nombre', p.t));
      // 2b: «Novedad» va en la lista de sellos (sobre la foto, como los demás), la última y dentro del
      // máximo de cuatro, como en snippets/ag-t2-catalogo-sellos.liquid; antes era un span en la foto
      var ss = sellos(p);
      if (p.n) ss.push(['nov', null, 'Novedad']);
      ss = ss.slice(0, 4);
      if (ss.length) {
        var ul = el('ul', 'agm-sellos');
        ul.setAttribute('role', 'list');
        ul.setAttribute('aria-label', 'Características del producto');
        ss.forEach(function (x) {
          var li = el('li', 'agm-sello agm-sello--' + x[0]);
          if (x[0] === 'sa') li.title = 'Sin azúcares añadidos. Contiene azúcares naturalmente presentes';
          // Sin icono: esta tarjeta siempre es de rejilla y allí no se ven (coste de DOM, 02/10, medida B)
          li.appendChild(el('span', null, x[2]));
          ul.appendChild(li);
        });
        info.appendChild(ul);
      }
      var precio = el('div', 'agk-precio');
      var pp = el('p', 'pd__precio');
      if (p.c > p.p) {
        var antes = el('s', 'pd__precio-antes');
        antes.appendChild(el('span', 'visually-hidden', 'Antes '));
        antes.appendChild(document.createTextNode(money(p.c)));
        pp.appendChild(antes);
      }
      pp.appendChild(el('span', 'pd__precio-main', money(p.p)));
      if (p.f) pp.appendChild(el('span', 'pd__peso', ' · ' + p.f));
      precio.appendChild(pp);
      if (p.k) precio.appendChild(el('p', 'pd__kg', p.k));
      info.appendChild(precio);
      var qa = anadir(p, linea);
      if (qa) info.appendChild(qa);
      c.appendChild(info);
      return c;
    }

    function skeletons(n) {
      grid.innerHTML = '';
      for (var i = 0; i < n; i++) {
        var d = document.createElement('div');
        d.className = 'bbx-skel';
        grid.appendChild(d);
      }
    }

    function reset() {
      grid.innerHTML = '';
      status.textContent = '';
      note.innerHTML = '';
      moreBtn.classList.remove('is-visible');
      root.classList.remove('is-vacio', 'is-parcial');
    }

    function pintarChips() {
      chips.forEach(function (c) {
        var on = c === chipActivo;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', String(on));
      });
    }

    function renderNote(parsed) {
      note.innerHTML = '';
      var fixes = parsed ? parsed.fixes.filter(function (f) { return !mismaRaiz(f.from, f.to); }) : [];
      if (fixes.length) {
        var fr = el('span', 'bbx-note-fix');
        fr.appendChild(document.createTextNode('Mostrando resultados de '));
        fixes.forEach(function (f, i) {
          if (i) fr.appendChild(document.createTextNode(', '));
          fr.appendChild(el('b', null, '«' + f.to + '»'));
        });
        note.appendChild(fr);
      }
      // Nutrientes: se dice de dónde sale (su tabla nutricional o el dato acreditado), nunca de una etiqueta de IA
      var TABLA = ' (según su tabla)';
      var visibles;
      if (chipActivo) visibles = [(chipActivo.dataset.label || chipActivo.textContent.trim()) + (chipActivo.hasAttribute('data-nutri') ? TABLA : '')];
      else visibles = (parsed ? parsed.concepts : []).filter(function (c) { return c.kind === 'tag' && !NO_EN_PANTALLA[c.v]; })
        .map(function (c) { return label(c.v) + (NUTRIENTE[c.v] ? TABLA : ''); });
      if (visibles.length) note.appendChild(el('span', 'bbx-note-q', 'Buscando: ' + visibles.join(' · ')));
    }

    /* ── sin resultados: texto honesto y búsquedas que sí existen (M19, C-A4) ── */
    function renderVacio(parsed, tipo) {
      grid.innerHTML = '';
      note.innerHTML = '';
      moreBtn.classList.remove('is-visible');
      root.classList.remove('is-parcial');
      root.classList.add('is-vacio');
      var consulta = parsed ? parsed.raw : '';
      var panel = el('div', 'bbx-vacio');
      var ideas = IDEAS_VACIO;
      if (tipo === 'gluten') {
        status.textContent = 'No señalamos productos «sin gluten».';
        panel.appendChild(el('p', 'bbx-vacio__texto', 'Consulta los alérgenos en la ficha de cada producto antes de comprar.'));
      } else {
        status.textContent = 'No tenemos nada con «' + consulta + '».';
        var alt = null;
        (parsed ? parsed.norm.split(' ') : []).some(function (w) { alt = ALTERNATIVAS[stem(w)] || ALTERNATIVAS[w]; return !!alt; });
        if (alt) ideas = alt;
        panel.appendChild(el('p', 'bbx-vacio__texto', alt ? 'Quizá te sirva:' : 'Prueba con un ingrediente o un plato:'));
      }
      var acc = el('div', 'bbx-vacio__acciones');
      ideas.forEach(function (x) {
        var b = el('button', 'bbx-chip bbx-sug', x[0]);
        b.type = 'button';
        b.setAttribute('data-bbx-sug', x[1]);
        acc.appendChild(b);
      });
      var cat = el('a', 'bbx-vacio__enlace', 'Ver todo el catálogo');
      cat.href = CATALOGO_URL;
      acc.appendChild(cat);
      panel.appendChild(acc);
      grid.appendChild(panel);
    }

    function render(items, parsed, falta) {
      pedirHojaTarjeta(); // medida E: red de seguridad si se pinta sin haber pasado por preparar() (respaldo por etiquetas)
      grid.innerHTML = '';
      root.classList.remove('is-vacio');
      root.classList.toggle('is-parcial', !!falta);
      var total = items.length;
      var shown = LIMIT ? items.slice(0, LIMIT) : items;
      var frag = document.createDocumentFragment();
      shown.forEach(function (it) { frag.appendChild(card(it.p)); });
      grid.appendChild(frag);
      renderNote(parsed);
      var nConcepts = parsed ? parsed.concepts.length : 1;
      var completos = items.filter(function (it) { return it.full; }).length;
      status.textContent = (falta ? 'No tenemos «' + falta.no.join('» ni «') + '». Esto es lo que hay con «' + falta.si.join('» y «') + '»: ' : '') +
        total + (total === 1 ? ' producto' : ' productos') +
        (!falta && nConcepts > 1 && completos && completos < total ? ' · ' + completos + ' con todo lo que buscas' : '') +
        (total > shown.length ? ' · mostrando ' + shown.length : '');
      if (LIMIT && total > LIMIT) {
        moreBtn.textContent = 'Ver los ' + total + ' productos →';
        moreBtn.href = pageUrl + '?q=' + encodeURIComponent(chipActivo ? (chipActivo.dataset.label || chipActivo.textContent.trim()) : (parsed ? parsed.raw : ''));
        moreBtn.classList.add('is-visible');
      } else {
        moreBtn.classList.remove('is-visible');
      }
    }

    // /search: la rejilla nativa manda. Aquí solo lo que ella no enseña, y solo si la consulta es una
    // necesidad o un nutriente (lo que la búsqueda de Shopify no entiende). Filas completas, sin huérfanas.
    function renderSearch(items, parsed) {
      var hayNecesidad = parsed.concepts.some(function (c) { return c.kind === 'tag'; });
      var nativos = Object.create(null);
      document.querySelectorAll('.product-grid__item a[href*="/products/"]').forEach(function (a) {
        var m = /\/products\/([^/?#]+)/.exec(a.getAttribute('href') || '');
        if (m) nativos[m[1]] = true;
      });
      var nuevos = items.filter(function (it) {
        var m = /\/products\/([^/?#]+)/.exec(it.p.u || '');
        return !(m && nativos[m[1]]);
      });
      if (!hayNecesidad || nuevos.length < 2) { root.hidden = true; return; }
      // Solo aparece si su sitio aún no se ve: aparecer debajo de lo que se está mirando no mueve nada (CLS 0).
      // Sin resultados nativos (data-cero) va antes de la rejilla de relleno si eso queda por debajo del pliegue
      // (móvil); si no (escritorio), detrás de ella, como siempre.
      if (root.hidden) {
        var alto = W.visualViewport ? W.visualViewport.height : innerHeight;
        var sig = root.nextElementSibling;
        var y = sig ? sig.getBoundingClientRect().top : Infinity;
        if (y < alto && root.hasAttribute('data-cero')) {
          var cw = root.closest('.collection-wrapper');
          if (cw) {
            cw.after(root);
            root.removeAttribute('data-cero');
            root.setAttribute('data-tras-relleno', '');
            var tit = root.querySelector('.bbx-searchtitle');
            if (tit) tit.textContent = 'Más ideas según lo que buscas';
            y = cw.getBoundingClientRect().bottom;
          }
        }
        if (y < alto) return;
      }
      render(nuevos, parsed);
      root.hidden = false;
      var cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 2;
      var cards = Array.prototype.slice.call(grid.children);
      // Dos filas completas como mucho (8 a 1440, 6 en tableta, 4 en móvil): es un complemento, no otra página.
      // Se quitan: la tarjeta del catálogo es display: flex y el atributo hidden no la ocultaría.
      var llenas = cards.length >= cols ? Math.min(cols * 2, cards.length - (cards.length % cols)) : cards.length;
      cards.slice(llenas).forEach(function (c) { c.remove(); });
    }

    // Tras pintar, que el cliente vea el primer resultado sin buscarlo: un solo desplazamiento por búsqueda
    // escrita (el campo queda arriba, a la vista) y uno por atajo. Instantáneo: el movimiento es de Astra.
    function acercar(origen) {
      if (mode === 'search') return;
      var primero = grid.firstElementChild || status;
      var alto = W.visualViewport ? W.visualViewport.height : innerHeight;
      var r = primero.getBoundingClientRect();
      if (r.top >= 0 && r.top < alto * 0.8) return;
      if (origen === 'texto') {
        if (desplazado) return;
        desplazado = true;
        (searchbox || status).scrollIntoView({ block: 'start' });
      } else {
        status.scrollIntoView({ block: 'start' });
      }
    }

    // ag_catalog_search solo con permiso de analítica (C-B4)
    function medir(q, n) {
      if (mode === 'search' || !q || q === ultimoEvento) return;
      clearTimeout(tA);
      tA = setTimeout(function () {
        var S = W.Shopify || {}, cp = S.customerPrivacy;
        if (!S.analytics || !S.analytics.publish || !cp || !cp.analyticsProcessingAllowed || !cp.analyticsProcessingAllowed()) return;
        ultimoEvento = q;
        S.analytics.publish('ag_catalog_search', { term: q, results: n });
      }, 1000);
    }

    function doSearch(q, opts) {
      var my = ++token;
      var parsed = parseQuery(q, { chip: !!opts.chip, sinEnfermedad: sinEnfermedad });
      var items = parsed.concepts.length ? runSearch(parsed.concepts, parsed) : [];
      var filtro = opts.chip && chipActivo ? filtroFamilia(chipActivo.dataset.f) : null;
      if (filtro) items = items.filter(function (it) { return filtro(it.p); });
      // Coincidencias de poco peso (solo una palabra desconocida a medias): no son resultados.
      if (items.length && items[0].s < 8) items = [];
      // «sin gluten»: la tienda no lo garantiza (D8), así que no se enseña nada como si lo fuera.
      if (parsed.sinGluten) items = [];
      if (my !== token) return;
      if (mode === 'search') { if (!items.length) { root.hidden = true; return; } renderSearch(items, parsed); return; }
      if (!items.length) {
        renderVacio(parsed, parsed.sinGluten ? 'gluten' : 'nada');
        medir(parsed.raw, 0);
      } else {
        render(items, parsed, opts.chip ? null : faltan(parsed, items));
        medir(opts.chip ? '' : parsed.raw, items.length);
      }
      acercar(opts.chip ? 'chip' : 'texto');
    }

    function search(q, opts) {
      opts = opts || {};
      q = (q || '').trim();
      token++;
      clearBtn.classList.toggle('is-visible', q.length > 0);
      if (q.length < 2) { reset(); pending = null; desplazado = false; return; }
      // Con dos letras solo buscamos si son el comienzo de un nombre del catálogo.
      // Sin ese prefijo aún no se afirma que la tienda no tenga resultados.
      if (q.length < 3 && prefijos && !prefijos[stem(norm(q))]) {
        reset(); status.textContent = 'Sigue escribiendo…'; pending = null; desplazado = false; return;
      }
      if (mode === 'full') {
        try { history.replaceState(null, '', location.pathname + '?q=' + encodeURIComponent(opts.visible || q)); } catch (e) { }
      }

      if (index && carrito && dictionaryReady()) { doSearch(q, opts); return; }

      if (indexFailed && !dictionaryReady()) {
        pending = { q: q, opts: opts };
        if (mode !== 'search') {
          status.textContent = 'Preparando el buscador…';
          skeletons(LIMIT || 12);
        }
        reintentarPendienteTrasDiccionario();
        return;
      }

      if (indexFailed) {
        // sin índice: al menos resolvemos las necesidades contra el servidor
        var parsed = parseQuery(q, { chip: !!opts.chip, sinEnfermedad: sinEnfermedad });
        var tags = parsed.concepts.filter(function (c) { return c.kind === 'tag' && !NUTRIENTE[c.v]; }).map(function (c) { return c.v; });
        if (!tags.length) {
          if (mode === 'search') { root.hidden = true; return; }
          status.textContent = 'El buscador no está disponible ahora mismo. Prueba con la lupa de arriba.';
          grid.innerHTML = '';
          return;
        }
        status.textContent = 'Buscando…';
        var serverToken = token;
        serverTagSearch(tags).then(function (items) {
          if (serverToken !== token) return;
          if (mode === 'search') renderSearch(items, parsed); else render(items, parsed);
        });
        return;
      }

      pending = { q: q, opts: opts };
      if (mode !== 'search') {
        status.textContent = 'Preparando el buscador…';
        skeletons(LIMIT || 12);
      }
      preparar().then(function () {
        buildFuzzyPool();
        if (pending) { var p = pending; pending = null; search(p.q, p.opts); }
      }).catch(function () {
        // loadIndex puede fallar antes de que llegue el diccionario: reintenta solo la última consulta tras él.
        reintentarPendienteTrasDiccionario();
      });
    }

    input.addEventListener('input', function () {
      token++;
      pending = null; // Un resultado pendiente no puede adelantarse al último texto durante el debounce.
      preparar().catch(function () { });
      chipActivo = null;
      pintarChips();
      root.classList.toggle('is-escribiendo', input.value.trim().length > 0);   // pliega los atajos en el mismo gesto
      if (mode !== 'search') { var nextStatus = input.value.trim().length >= 2 ? 'Buscando…' : ''; if (status.textContent !== nextStatus) status.textContent = nextStatus; }
      clearTimeout(timer);
      timer = setTimeout(function () { search(input.value); }, 220);
    });

    input.addEventListener('focus', function () {
      preparar().catch(function () { });
    }, { once: true });

    clearBtn.addEventListener('click', function () {
      input.value = '';
      chipActivo = null;
      pintarChips();
      root.classList.remove('is-escribiendo');
      search('');
      input.focus();
    });

    // Un atajo a la vez: el campo enseña su texto visible y se busca por lo que lleva dentro (data-q).
    function activar(chip) {
      chipActivo = chip;
      pintarChips();
      root.classList.remove('is-escribiendo');
      if (!chip) { input.value = ''; search(''); return; }
      var visible = chip.dataset.label || chip.textContent.trim();
      input.value = visible;
      search(chip.dataset.q || visible, { chip: true, visible: visible });
    }
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () { preparar().catch(function () { }); activar(chipActivo === chip ? null : chip); });
    });

    // Sugerencias del estado vacío: son búsquedas normales
    grid.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-bbx-sug]');
      if (!b) return;
      input.value = b.textContent.trim();
      chipActivo = null;
      pintarChips();
      desplazado = true;
      search(b.getAttribute('data-bbx-sug'));
    });

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Se busca dentro del propio componente, no por id: puede haber dos buscadores en la página.
        var group = btn.previousElementSibling;
        if (!group || !group.classList.contains('bbx-chips')) group = root.querySelector('.bbx-chips[data-bbx-collapsible]');
        if (!group) return;
        var collapsed = group.classList.toggle('is-collapsed');
        btn.textContent = collapsed ? btn.dataset.more : btn.dataset.less;
        btn.setAttribute('aria-expanded', String(!collapsed));
      });
    });

    if (mode === 'full' || mode === 'search') {
      var q0 = root.dataset.q || new URLSearchParams(location.search).get('q');
      if (q0) {
        // «Ver los N productos» desde un atajo llega con su texto visible: se reabre el mismo atajo.
        var igual = chips.filter(function (c) { return norm(c.dataset.label || c.textContent) === norm(q0); })[0];
        if (igual) activar(igual);
        else { input.value = q0; search(q0); }
      }
    } else if (input.value.trim()) {
      // Se escribió antes de que el motor llegara (carga perezosa): se busca lo que ya hay en el campo.
      search(input.value);
    }
    if (W.__bbxPendingChip && W.__bbxPendingChip.closest('[data-bbx]') === root) {
      var primerChip = W.__bbxPendingChip;
      W.__bbxPendingChip = null;
      primerChip.click();
    }
    programarPreparacionInline();
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bbx]'), init);
  }

  function warmup() {
    // full y /search conservan warmup; inline no carga índice antes de interacción.
    if (!document.querySelector('[data-bbx]:not([data-mode="inline"])')) return;
    var go = function () { loadIndex().catch(function () { }); };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 3000 });
    else setTimeout(go, 1200);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { initAll(); warmup(); });
    } else {
      initAll(); warmup();
    }
    document.addEventListener('shopify:section:load', initAll);
  }

  // Banco de pruebas en Node
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      norm: norm,
      stem: stem,
      dist: dist,
      label: label,
      parseQuery: parseQuery,
      runSearch: runSearch,
      faltan: faltan,
      filtroFamilia: filtroFamilia,
      NO_EN_PANTALLA: NO_EN_PANTALLA,
      cargarIndiceDePrueba: function (items) {
        index = decorate(items);
        fuzzyPool = null;
        buildFuzzyPool();
        return index.length;
      }
    };
  }
})();


